/**
 * Permissions Management Page
 *
 * RBAC (Role-Based Access Control) management for GRC Platform:
 * - View and manage permissions
 * - Create, edit, and delete custom roles
 * - Assign roles to users
 * - Set user-level permission overrides
 *
 * GitHub Issue: #138
 */

import { useState, useEffect, useCallback } from 'react'
import { api, Permission, Role, UserPermissionDetails } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  Shield,
  Users,
  Key,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  Loader2,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  UserCheck,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
} from 'lucide-react'

type TabType = 'permissions' | 'roles' | 'users'

interface UserWithPermissions {
  id: string
  username: string
  email: string
  role: string
  first_name?: string
  last_name?: string
}

export default function Permissions() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('roles')

  // Data state
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [groupedPermissions, setGroupedPermissions] = useState<Record<string, Permission[]>>({})
  const [roles, setRoles] = useState<Role[]>([])
  const [users, setUsers] = useState<UserWithPermissions[]>([])
  const [selectedUser, setSelectedUser] = useState<UserWithPermissions | null>(null)
  const [userPermissionDetails, setUserPermissionDetails] = useState<UserPermissionDetails | null>(null)

  // Loading state
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [seeding, setSeeding] = useState(false)

  // UI state
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false)
  const [showEditRoleModal, setShowEditRoleModal] = useState(false)
  const [showUserPermissionsModal, setShowUserPermissionsModal] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)

  // Form state for role creation/editing
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permission_ids: [] as string[],
  })

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true)

      // Load permissions
      const permResponse = await api.getPermissions()
      if (permResponse.success) {
        setPermissions(permResponse.permissions)
        setGroupedPermissions(permResponse.grouped)
      }

      // Load roles
      const rolesResponse = await api.getRoles()
      if (rolesResponse.success) {
        setRoles(rolesResponse.roles)
      }

      // Load users for user tab
      const usersResponse = await api.getUsers()
      if (usersResponse.success) {
        setUsers(usersResponse.users as UserWithPermissions[])
      }
    } catch (error) {
      console.error('Failed to load permissions data:', error)
      toast.error('Failed to load permissions data')
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Seed default permissions and roles
  const handleSeedPermissions = async () => {
    const confirmed = await confirm({
      title: 'Seed Default Permissions',
      message:
        'This will create the default permissions and roles for your organization. Existing permissions and roles will not be affected. Continue?',
      confirmText: 'Seed Permissions',
      type: 'info',
    })

    if (!confirmed) return

    try {
      setSeeding(true)
      const response = await api.seedPermissions()
      if (response.success) {
        toast.success(
          `Seeded ${response.permissions_created} permissions and ${response.roles_created} roles`
        )
        loadData()
      } else {
        toast.error('Failed to seed permissions')
      }
    } catch (error) {
      console.error('Failed to seed permissions:', error)
      toast.error('Failed to seed permissions')
    } finally {
      setSeeding(false)
    }
  }

  // Toggle resource expansion
  const toggleResource = (resource: string) => {
    setExpandedResources((prev) => {
      const next = new Set(prev)
      if (next.has(resource)) {
        next.delete(resource)
      } else {
        next.add(resource)
      }
      return next
    })
  }

  // Create role
  const handleCreateRole = async () => {
    if (!roleForm.name.trim()) {
      toast.error('Role name is required')
      return
    }

    try {
      setSaving(true)
      const response = await api.createRole({
        name: roleForm.name.trim(),
        description: roleForm.description.trim() || undefined,
        permission_ids: roleForm.permission_ids,
      })

      if (response.success) {
        toast.success('Role created successfully')
        setShowCreateRoleModal(false)
        setRoleForm({ name: '', description: '', permission_ids: [] })
        loadData()
      } else {
        toast.error('Failed to create role')
      }
    } catch (error) {
      console.error('Failed to create role:', error)
      toast.error('Failed to create role')
    } finally {
      setSaving(false)
    }
  }

  // Update role
  const handleUpdateRole = async () => {
    if (!editingRole) return

    try {
      setSaving(true)
      const response = await api.updateRole(editingRole.id, {
        name: roleForm.name.trim() || undefined,
        description: roleForm.description.trim() || undefined,
        permission_ids: roleForm.permission_ids,
      })

      if (response.success) {
        toast.success('Role updated successfully')
        setShowEditRoleModal(false)
        setEditingRole(null)
        setRoleForm({ name: '', description: '', permission_ids: [] })
        loadData()
      } else {
        toast.error('Failed to update role')
      }
    } catch (error) {
      console.error('Failed to update role:', error)
      toast.error('Failed to update role')
    } finally {
      setSaving(false)
    }
  }

  // Delete role
  const handleDeleteRole = async (role: Role) => {
    const confirmed = await confirm({
      title: 'Delete Role',
      message: `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
    })

    if (!confirmed) return

    try {
      const response = await api.deleteRole(role.id)
      if (response.success) {
        toast.success('Role deleted successfully')
        loadData()
      } else {
        toast.error('Failed to delete role')
      }
    } catch (error) {
      console.error('Failed to delete role:', error)
      toast.error('Failed to delete role')
    }
  }

  // Load user permissions
  const handleViewUserPermissions = async (userItem: UserWithPermissions) => {
    try {
      setSelectedUser(userItem)
      setShowUserPermissionsModal(true)

      const response = await api.getUserPermissions(userItem.id)
      if (response.success) {
        setUserPermissionDetails(response)
      }
    } catch (error) {
      console.error('Failed to load user permissions:', error)
      toast.error('Failed to load user permissions')
    }
  }

  // Assign role to user
  const handleAssignRole = async (userId: string, roleId: string) => {
    try {
      const response = await api.assignUserRole(userId, roleId)
      if (response.success) {
        toast.success('Role assigned successfully')
        if (selectedUser) {
          const refreshResponse = await api.getUserPermissions(selectedUser.id)
          if (refreshResponse.success) {
            setUserPermissionDetails(refreshResponse)
          }
        }
      } else {
        toast.error('Failed to assign role')
      }
    } catch (error) {
      console.error('Failed to assign role:', error)
      toast.error('Failed to assign role')
    }
  }

  // Remove role from user
  const handleRemoveRole = async (userId: string, roleId: string) => {
    try {
      const response = await api.removeUserRole(userId, roleId)
      if (response.success) {
        toast.success('Role removed successfully')
        if (selectedUser) {
          const refreshResponse = await api.getUserPermissions(selectedUser.id)
          if (refreshResponse.success) {
            setUserPermissionDetails(refreshResponse)
          }
        }
      } else {
        toast.error('Failed to remove role')
      }
    } catch (error) {
      console.error('Failed to remove role:', error)
      toast.error('Failed to remove role')
    }
  }

  // Set permission override
  const handleSetPermissionOverride = async (
    userId: string,
    permissionId: string,
    grantType: 'grant' | 'deny'
  ) => {
    try {
      const response = await api.setUserPermissionOverride(userId, {
        permission_id: permissionId,
        grant_type: grantType,
      })
      if (response.success) {
        toast.success(`Permission ${grantType === 'grant' ? 'granted' : 'denied'} successfully`)
        if (selectedUser) {
          const refreshResponse = await api.getUserPermissions(selectedUser.id)
          if (refreshResponse.success) {
            setUserPermissionDetails(refreshResponse)
          }
        }
      } else {
        toast.error('Failed to set permission override')
      }
    } catch (error) {
      console.error('Failed to set permission override:', error)
      toast.error('Failed to set permission override')
    }
  }

  // Remove permission override
  const handleRemovePermissionOverride = async (userId: string, permissionId: string) => {
    try {
      const response = await api.removeUserPermissionOverride(userId, permissionId)
      if (response.success) {
        toast.success('Permission override removed')
        if (selectedUser) {
          const refreshResponse = await api.getUserPermissions(selectedUser.id)
          if (refreshResponse.success) {
            setUserPermissionDetails(refreshResponse)
          }
        }
      } else {
        toast.error('Failed to remove override')
      }
    } catch (error) {
      console.error('Failed to remove override:', error)
      toast.error('Failed to remove override')
    }
  }

  // Toggle permission in role form
  const togglePermission = (permissionId: string) => {
    setRoleForm((prev) => {
      const hasPermission = prev.permission_ids.includes(permissionId)
      return {
        ...prev,
        permission_ids: hasPermission
          ? prev.permission_ids.filter((id) => id !== permissionId)
          : [...prev.permission_ids, permissionId],
      }
    })
  }

  // Filter users by search
  const filteredUsers = users.filter((u) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      u.username.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower) ||
      (u.first_name && u.first_name.toLowerCase().includes(searchLower)) ||
      (u.last_name && u.last_name.toLowerCase().includes(searchLower))
    )
  })

  // Check if user is admin
  const isAdmin = user?.role === 'admin'

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-blue-600" />
            Permissions & Roles
          </h1>
          <p className="text-gray-600 mt-1">
            Manage role-based access control for your organization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          {isAdmin && permissions.length === 0 && (
            <button
              onClick={handleSeedPermissions}
              disabled={seeding}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {seeding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Initialize Permissions
            </button>
          )}
        </div>
      </div>

      {/* No permissions notice */}
      {permissions.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-800">Permissions Not Initialized</h3>
            <p className="text-yellow-700 text-sm mt-1">
              Click "Initialize Permissions" to set up the default permissions and roles for your
              organization. This is required before you can manage access control.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('roles')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Key className="h-4 w-4" />
            Roles ({roles.length})
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'permissions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Shield className="h-4 w-4" />
            Permissions ({permissions.length})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'users'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users className="h-4 w-4" />
            User Permissions ({users.length})
          </button>
        </nav>
      </div>

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div>
          {/* Create Role Button */}
          {isAdmin && (
            <div className="mb-4">
              <button
                onClick={() => {
                  setRoleForm({ name: '', description: '', permission_ids: [] })
                  setShowCreateRoleModal(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Role
              </button>
            </div>
          )}

          {/* Roles List */}
          <div className="space-y-4">
            {roles.map((role) => (
              <div
                key={role.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
                      {role.is_system === 1 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                          System
                        </span>
                      )}
                      {role.is_active === 0 && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-gray-600 text-sm mt-1">{role.description}</p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-1">
                      {role.permissions?.slice(0, 10).map((perm) => (
                        <span
                          key={perm.id}
                          className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                        >
                          {perm.name}
                        </span>
                      ))}
                      {role.permissions && role.permissions.length > 10 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                          +{role.permissions.length - 10} more
                        </span>
                      )}
                    </div>
                  </div>
                  {isAdmin && role.is_system === 0 && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => {
                          setEditingRole(role)
                          setRoleForm({
                            name: role.name,
                            description: role.description || '',
                            permission_ids: role.permissions?.map((p) => p.id) || [],
                          })
                          setShowEditRoleModal(true)
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit role"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRole(role)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete role"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {roles.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No roles defined yet</p>
                <p className="text-sm">Initialize permissions to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div className="space-y-4">
          {Object.entries(groupedPermissions).map(([resource, perms]) => (
            <div key={resource} className="bg-white border border-gray-200 rounded-lg">
              <button
                onClick={() => toggleResource(resource)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  {expandedResources.has(resource) ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="font-medium text-gray-900 capitalize">{resource}</span>
                  <span className="text-sm text-gray-500">({perms.length} permissions)</span>
                </div>
              </button>
              {expandedResources.has(resource) && (
                <div className="border-t border-gray-100 px-4 py-2">
                  <div className="space-y-2">
                    {perms.map((perm) => (
                      <div
                        key={perm.id}
                        className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded"
                      >
                        <div>
                          <span className="font-mono text-sm text-gray-800">{perm.name}</span>
                          {perm.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                          )}
                        </div>
                        {perm.is_system === 1 ? (
                          <span className="text-xs text-gray-400">System</span>
                        ) : (
                          <span className="text-xs text-blue-600">Custom</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {Object.keys(groupedPermissions).length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No permissions defined yet</p>
              <p className="text-sm">Initialize permissions to get started</p>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Users List */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Legacy Role
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((userItem) => (
                  <tr key={userItem.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-medium">
                            {userItem.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {userItem.first_name && userItem.last_name
                              ? `${userItem.first_name} ${userItem.last_name}`
                              : userItem.username}
                          </div>
                          <div className="text-sm text-gray-500">{userItem.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          userItem.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : userItem.role === 'contributor'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {userItem.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleViewUserPermissions(userItem)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 ml-auto"
                      >
                        <UserCheck className="h-4 w-4" />
                        Manage Permissions
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Role Modal */}
      {showCreateRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Create New Role</h2>
              <button
                onClick={() => setShowCreateRoleModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Risk Manager"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Describe what this role is for..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                    {Object.entries(groupedPermissions).map(([resource, perms]) => (
                      <div key={resource} className="border-b border-gray-100 last:border-0">
                        <div className="px-3 py-2 bg-gray-50 font-medium text-sm capitalize">
                          {resource}
                        </div>
                        <div className="px-3 py-2 space-y-1">
                          {perms.map((perm) => (
                            <label
                              key={perm.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={roleForm.permission_ids.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                                className="rounded text-blue-600"
                              />
                              <span className="text-sm">{perm.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateRoleModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                disabled={saving || !roleForm.name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditRoleModal && editingRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit Role: {editingRole.name}</h2>
              <button
                onClick={() => {
                  setShowEditRoleModal(false)
                  setEditingRole(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                    {Object.entries(groupedPermissions).map(([resource, perms]) => (
                      <div key={resource} className="border-b border-gray-100 last:border-0">
                        <div className="px-3 py-2 bg-gray-50 font-medium text-sm capitalize">
                          {resource}
                        </div>
                        <div className="px-3 py-2 space-y-1">
                          {perms.map((perm) => (
                            <label
                              key={perm.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={roleForm.permission_ids.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                                className="rounded text-blue-600"
                              />
                              <span className="text-sm">{perm.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditRoleModal(false)
                  setEditingRole(null)
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Permissions Modal */}
      {showUserPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">User Permissions</h2>
                <p className="text-sm text-gray-500">
                  {selectedUser.first_name
                    ? `${selectedUser.first_name} ${selectedUser.last_name}`
                    : selectedUser.username}{' '}
                  ({selectedUser.email})
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUserPermissionsModal(false)
                  setSelectedUser(null)
                  setUserPermissionDetails(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {userPermissionDetails ? (
                <div className="space-y-6">
                  {/* Legacy Role */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Legacy Role</h3>
                    <span
                      className={`px-3 py-1 text-sm rounded-full ${
                        userPermissionDetails.legacy_role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : userPermissionDetails.legacy_role === 'contributor'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {userPermissionDetails.legacy_role}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Legacy role provides base permissions. Assign additional roles below for
                      custom access.
                    </p>
                  </div>

                  {/* Assigned Roles */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Assigned Roles</h3>
                    <div className="flex flex-wrap gap-2">
                      {userPermissionDetails.assigned_roles.map((role) => (
                        <div
                          key={role.id}
                          className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full"
                        >
                          <span className="text-sm">{role.name}</span>
                          {isAdmin && (
                            <button
                              onClick={() => handleRemoveRole(selectedUser.id, role.id)}
                              className="text-blue-400 hover:text-blue-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                      {userPermissionDetails.assigned_roles.length === 0 && (
                        <span className="text-gray-400 text-sm">No additional roles assigned</span>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="mt-3">
                        <select
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAssignRole(selectedUser.id, e.target.value)
                              e.target.value = ''
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="">+ Assign role...</option>
                          {roles
                            .filter(
                              (r) =>
                                !userPermissionDetails.assigned_roles.find((ar) => ar.id === r.id)
                            )
                            .map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Permission Overrides */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Permission Overrides</h3>
                    {userPermissionDetails.overrides.length > 0 ? (
                      <div className="space-y-2">
                        {userPermissionDetails.overrides.map((override) => (
                          <div
                            key={`${override.user_id}-${override.permission_id}`}
                            className={`flex items-center justify-between px-3 py-2 rounded ${
                              override.grant_type === 'grant'
                                ? 'bg-green-50 border border-green-200'
                                : 'bg-red-50 border border-red-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {override.grant_type === 'grant' ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                              <span className="text-sm font-mono">{override.permission_name}</span>
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  override.grant_type === 'grant'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {override.grant_type}
                              </span>
                            </div>
                            {isAdmin && (
                              <button
                                onClick={() =>
                                  handleRemovePermissionOverride(
                                    selectedUser.id,
                                    override.permission_id
                                  )
                                }
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No permission overrides set</p>
                    )}
                  </div>

                  {/* Effective Permissions */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Effective Permissions ({userPermissionDetails.effective_permissions.length})
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <div className="flex flex-wrap gap-1">
                        {userPermissionDetails.effective_permissions.map((perm) => (
                          <span
                            key={perm}
                            className="px-2 py-0.5 text-xs bg-white border border-gray-200 text-gray-700 rounded font-mono"
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Add Permission Override (Admin only) */}
                  {isAdmin && (
                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        Add Permission Override
                      </h3>
                      <div className="flex gap-2">
                        <select
                          id="override-permission"
                          className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                          defaultValue=""
                        >
                          <option value="">Select permission...</option>
                          {permissions.map((perm) => (
                            <option key={perm.id} value={perm.id}>
                              {perm.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => {
                            const select = document.getElementById(
                              'override-permission'
                            ) as HTMLSelectElement
                            if (select.value) {
                              handleSetPermissionOverride(selectedUser.id, select.value, 'grant')
                              select.value = ''
                            }
                          }}
                          className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm flex items-center gap-1"
                        >
                          <Unlock className="h-3 w-3" />
                          Grant
                        </button>
                        <button
                          onClick={() => {
                            const select = document.getElementById(
                              'override-permission'
                            ) as HTMLSelectElement
                            if (select.value) {
                              handleSetPermissionOverride(selectedUser.id, select.value, 'deny')
                              select.value = ''
                            }
                          }}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm flex items-center gap-1"
                        >
                          <Lock className="h-3 w-3" />
                          Deny
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
